import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../services/api.ts";
import toast from "react-hot-toast";

const DB_URI = process.env.REACT_APP_DB_URI;

// --- API Functions ---

const fetchAllUsers = async () => {
    const { data } = await api.get(`${DB_URI}/users/admin/`);
    return data;
};

const deleteUser = async (id: number | string) => {
    const response = await api.delete(`${DB_URI}/users/admin/${id}/`);
    return response;
};

const fetchVerificationRequests = async () => {
    const { data } = await api.get(`${DB_URI}/users/admin/get_all_verification_requests/`);
    return data;
};

const verifyOrganizationStatus = async ({ id, status }: { id: string | number, status: boolean }) => {
    const { data } = await api.post(`${DB_URI}/users/profile/${id}/verify/${status}/`, {});
    return data;
};

const fetchAllOrgs = async () => {
    const { data } = await api.get(`${DB_URI}/users/admin/organization-users/`);
    return data;
};

const fetchUserDetails = async (id: string | number) => {
    if (!id) return null;
    const { data } = await api.get(`${DB_URI}/users/admin/${id}/`);
    return data;
};

const resetUserPassword = async ({ userId, newPassword, confirmPassword }: any) => {
    const { data } = await api.post(`${DB_URI}/users/password-management/reset-password/`, {
        user_id: userId,
        new_password: newPassword,
        confirm_password: confirmPassword
    });
    return data;
};

const fetchUserQueries = async () => {
    const { data } = await api.get(`${DB_URI}/users/query/`);
    return data;
};

const updateQueryStatus = async ({ id, status }: { id: string, status: string }) => {
    const formData = new FormData();
    formData.append('status', status);
    
    // Override headers for this specific request
    const { data } = await api.patch(`${DB_URI}/users/query/${id}/`, formData, {
        headers: {
            // Remove the default Content-Type
            'Content-Type': 'multipart/form-data',
        },
    });
    
    return data;
};

const fetchAllInfluencers = async () => {
    const { data } = await api.get(`${DB_URI}/users/admin/influencer-users/`);
    return data;
};

const verifyInfluencer = async ({ id, status }: { id: string, status: boolean }) => {
    const { data } = await api.post(`${DB_URI}/users/profile/${id}/verify-influencer/${status}/`, {});
    return data;
};

const toggleCampaignStatus = async ({ id, status }: { id: string, status: boolean }) => {
    const formData = new FormData();
    formData.append('is_enabled_by_admin', status.toString());
    const { data } = await api.patch(`${DB_URI}/campaigns/${id}/set_enabled/`, formData);
    return data;
};

// --- Hooks ---

export const useResetUserPassword = () => {
    return useMutation({
        mutationFn: resetUserPassword,
        onSuccess: () => {
            toast.success("Password reset successfully!");
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message || "Failed to reset password");
        }
    });
};

export const useAllUsers = () => {
    return useQuery({
        queryKey: ['admin', 'users'],
        queryFn: fetchAllUsers
    });
};

export const useDeleteUser = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteUser,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'influencers'] });
            toast.success("User deleted successfully");
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message || "Failed to delete user");
        }
    });
};

export const useVerificationRequests = () => {
    return useQuery({
        queryKey: ['admin', 'verificationRequests'],
        queryFn: fetchVerificationRequests
    });
};

export const useVerifyOrganizationStatus = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: verifyOrganizationStatus,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'verificationRequests'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] });
            toast.success("Organization status updated successfully");
        },
        onError: (error: any) => {
            toast.error("Failed to update verification status");
        }
    });
};

export const useAllOrganizations = () => {
    return useQuery({
        queryKey: ['admin', 'organizations'],
        queryFn: fetchAllOrgs
    });
};

export const useUserDetails = (id: string | number) => {
    return useQuery({
        queryKey: ['admin', 'userDetails', id],
        queryFn: () => fetchUserDetails(id),
        enabled: !!id
    });
};

export const useAllQueries = () => {
    return useQuery({
        queryKey: ['admin', 'allQueries'],
        queryFn: fetchUserQueries
    });
};

export const useUpdateQueryStatus = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: updateQueryStatus,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'allQueries'] });
            toast.success("Query status updated");
        },
        onError: () => {
            toast.error("Failed to update query status");
        }
    });
};

export const useAllInfluencers = () => {
    return useQuery({
        queryKey: ['admin', 'influencers'],
        queryFn: fetchAllInfluencers
    });
};

export const useVerifyInfluencer = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: verifyInfluencer,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'verificationRequests'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'influencers'] });
            toast.success("Influencer verified successfully");
        },
        onError: () => {
            toast.error("Failed to verify influencer");
        }
    });
};

export const useToggleCampaignStatus = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: toggleCampaignStatus,
        onSuccess: () => {
            // Invalidate campaigns query - need to coordinate key with useCampaigns.ts
            queryClient.invalidateQueries({ queryKey: ['campaigns'] });
            toast.success("Campaign status updated");
        },
        onError: () => {
            toast.error("Failed to update campaign status");
        }
    });
};
